import {FC, useState} from 'react';

import MarketPositionRow from '@/pages/(shared)/positions/new/components/market-position/marketPositionRow';
import MarketPositionsDetails from '@/pages/(shared)/positions/new/components/market-position/marketPositionsDetails';
import {useCreateMarketPositions} from '@/pages/(shared)/positions/new/providers/createMarketPositionsProvider';

import Page from '@/components/Page';

/**
 * Wholesale positions form
 * @returns {JSX.Element}
 */
const MarketPositionsForm: FC = () => {
  const {
    positions,
    handleAddPosition: addPosition,
    handleUpdatePosition: updatePosition,
    handleDeletePosition: deletePosition,
    totals,
    acknowledging,
    validatePositions,
    handleSubmitPositions,
    handleDuplicatePosition,
  } = useCreateMarketPositions();
  const [showInstructions, setShowInstructions] = useState(false);

  const handleReviewPositions = () => {
    const isValid = validatePositions();
    if (isValid) {
      handleSubmitPositions();
    } else {
      // scroll to top
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  };

  /**
   * Toggles the instructions modal
   */
  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
  };

  if (acknowledging) {
    return <MarketPositionsDetails />;
  }

  return (
    <Page currentPage="Create Market Positions">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-12">
          {!acknowledging ? (
            <div className="box">
              {/* HEADER */}
              <div className="box-header !block xxl:!flex">
                <div className="h5 mb-0 block items-center sm:flex">
                  <div>
                    <img src={'/images/logo/logo.png'} alt="" className="w-10" />
                  </div>
                  <div className="ms-0 mt-2 sm:ms-2 sm:mt-0">
                    <span className="form-control !rounded-md !p-0">{new Date().toLocaleDateString()}</span>
                  </div>
                  <div className="mx-2">:</div>
                  <div className="sm:mt-0">
                    <span className="form-control !rounded-md !p-0">{new Date().toLocaleTimeString()}</span>
                  </div>
                </div>
                <div className="!mt-2 ms-auto xxl:!mt-0">
                  <button type="button" className="ti-btn me-2 bg-primary !px-2 !py-1 !text-[0.75rem] text-white">
                    Save As PDF<i className="ri-file-pdf-line ms-1 inline-block align-middle"></i>
                  </button>
                  <button
                    aria-label="Instructions"
                    type="button"
                    className="ti-btn ti-btn-sm ti-btn-info me-2"
                    onClick={toggleInstructions}
                  >
                    <i className="ri-information-line"></i>
                  </button>
                  <button aria-label="button" type="button" className="ti-btn ti-btn-sm ti-btn-secondary me-2">
                    <i className="bi bi-plus-lg"></i>
                  </button>
                  <button aria-label="button" type="button" className="ti-btn ti-btn-sm ti-btn-success me-2">
                    <i className="bi bi-download"></i>
                  </button>
                </div>
              </div>
              {/* BODY */}
              <div className="box-body">
                {showInstructions && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-10">
                    <div className="relative w-full max-w-2xl rounded-lg bg-black p-6 shadow-lg">
                      <button
                        onClick={toggleInstructions}
                        className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                      >
                        <i className="ri-close-line text-xl"></i>
                      </button>
                      <h6 className="mb-4 text-xl font-semibold">Position Creation Instructions:</h6>
                      <ol className="list-decimal pl-6">
                        <li>Click &quot;Add Position&quot; to create a new position row.</li>
                        <li>For each position, select the market, brand, and product.</li>
                        <li>Set the quantity and markup percentage for your position.</li>
                        <li>Review the calculated values (wholesale price, total cost, market value, and profit).</li>
                        <li>Add multiple positions as needed to build your portfolio.</li>
                        <li>The subtotal and total will automatically update based on your positions.</li>
                      </ol>
                      <p className="mt-4 text-sm text-gray-600">
                        Note: Ensure all fields are filled correctly before finalizing your positions.
                      </p>
                    </div>
                  </div>
                )}
                {/* POSITIONS TABLE */}
                {positions.length === 0 ? (
                  <div className="text-center">
                    <p className="text-gray-500">Please add a position to get started.</p>
                    <button className="ti-btn ti-btn-light mt-4 !font-medium" onClick={addPosition}>
                      <i className="bi bi-plus-lg"></i> Add Position
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-y-6 sm:gap-x-6">
                    <div className="col-span-12 xl:col-span-12">
                      <div className="table-responsive">
                        <table className="mt-3 table whitespace-nowrap border dark:border-defaultborder/10">
                          <thead>
                            <tr>
                              <th scope="row">Market</th>
                              <th scope="row">Brand</th>
                              <th scope="row">Product</th>

                              <th scope="row">Retail</th>
                              <th scope="row">Distributor</th>
                              <th scope="row">Pending</th>
                              <th scope="row">Total Cost</th>
                              <th scope="row">Markup</th>
                              <th scope="row">Market Value</th>
                              <th scope="row">Profit</th>
                              <th scope="row">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {positions.map((position, index) => (
                              <MarketPositionRow
                                key={index}
                                index={index}
                                position={position}
                                onDuplicatePosition={data => handleDuplicatePosition(index, data)}
                                onUpdatePosition={data => updatePosition(index, data)}
                                onDelete={() => deletePosition(index)}
                              />
                            ))}
                            <tr>
                              <td colSpan={10} className="border-bottom-0">
                                <button className="ti-btn ti-btn-light !font-medium" onClick={addPosition}>
                                  <i className="bi bi-plus-lg"></i> Add Position
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/* FOOTER */}
                    <div className="col-span-12 xl:col-span-12">
                      <div className="flex justify-end">
                        <table className="table-sm table-borderless mb-0 table whitespace-nowrap dark:border-defaultborder/10">
                          <tbody>
                            <tr>
                              <th scope="row">
                                <div className="text-sm font-semibold">Potential Profit :</div>
                              </th>
                              <td>
                                <span
                                  className="form-control !rounded-md !p-0 font-bold"
                                  style={{
                                    color: 'green',
                                  }}
                                >
                                  $
                                  {totals.potentialProfit.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">
                                <div className="text-sm font-semibold">Market Value :</div>
                              </th>
                              <td>
                                <span className="form-control !rounded-md !p-0">
                                  $
                                  {totals.marketValue.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">
                                <div className="text-sm font-semibold">Sub Total :</div>
                              </th>
                              <td>
                                <span className="form-control !rounded-md !p-0">
                                  $
                                  {totals.subtotal.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="box-footer text-end">
                {positions.length > 0 && (
                  <button type="button" className="ti-btn !mb-0 bg-primary text-white" onClick={handleReviewPositions}>
                    Review Position{positions.length > 1 ? 's' : ''}{' '}
                    <i className="ri-send-plane-2-line ms-1 inline-block align-middle"></i>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <MarketPositionsDetails />
          )}
        </div>
      </div>
    </Page>
  );
};

export default MarketPositionsForm;
